import { useState, useEffect } from "react";
import { useRoute } from "wouter";
import { SEO } from "@/components/SEO";
import { CheckCircle, Clock, Shield, FileText, ArrowRight, Sparkles, X } from "lucide-react";

interface InvoiceAttachment {
  item_id: string;
  filename: string;
  mime_type: string;
  public_url: string;
}

interface InvoiceItem {
  id: string;
  seq_num: number;
  version: string | null;
  category: string | null;
  title: string;
  description: string | null;
  source_context: string | null;
  source_date: string | null;
  status: string;
  video_url: string | null;
  attachments: InvoiceAttachment[];
}

interface InvoiceData {
  invoiceNumber: string;
  clientName: string;
  projectName: string;
  issuedDate: string;
  dueDate: string;
  paymentTerms?: string;
  subtotal: number;
  total: number;
  paymentUrl: string;
  isDiscounted: boolean;
  discountPercent: number;
  discountDeadline: string;
  originalTotal: number;
  savings: number;
  lineItems: {
    description: string;
    detail: string;
    amount: number;
  }[];
  items?: InvoiceItem[];
  isPaid: boolean;
}

export default function InvoicePaymentPage() {
  const [, params] = useRoute("/pay/:invoiceNumber");
  const invoiceNumber = params?.invoiceNumber || "";
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedAttachment, setExpandedAttachment] = useState<InvoiceAttachment | null>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const paymentSuccess = urlParams.get("status") === "success";

  useEffect(() => {
    if (!invoiceNumber) return;

    fetch(`/api/pay/${invoiceNumber}`)
      .then((res) => {
        if (!res.ok) throw new Error("Invoice not found");
        return res.json();
      })
      .then((data) => {
        setInvoice(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Invoice not found");
        setLoading(false);
      });
  }, [invoiceNumber]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-700 rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <FileText className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold text-slate-950 mb-2">Invoice Not Found</h1>
          <p className="text-slate-500 text-sm">This invoice does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  // Success state
  if (paymentSuccess || invoice.isPaid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <SEO title={`Payment Received — ${invoice.invoiceNumber}`} description="Payment confirmed" />
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-xl border border-slate-200 p-10 shadow-sm">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-950 mb-2">Payment Received</h1>
            <p className="text-slate-600 mb-6">
              Thank you, {invoice.clientName}. Your payment for invoice {invoice.invoiceNumber} has been confirmed.
            </p>
            <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Amount Paid</p>
              <p className="text-3xl font-bold text-emerald-700">${invoice.total.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
            </div>
            <p className="text-xs text-slate-500 mt-6">A receipt has been sent to your email.</p>
          </div>
          <p className="text-xs text-slate-500 mt-8">
            Better Systems AI &bull; bettersystems.ai
          </p>
        </div>
      </div>
    );
  }

  const formatCurrency = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 py-12">
      <SEO title={`Invoice ${invoice.invoiceNumber} — Better Systems AI`} description="Secure invoice payment" />

      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xl font-semibold tracking-tight text-blue-700">Better Systems AI</div>
            <p className="text-sm text-slate-500">Custom software, AI systems, and production app launches</p>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-xs uppercase tracking-wider text-slate-500">Payment Page</p>
            <h1 className="text-sm text-slate-700 font-mono">Invoice {invoice.invoiceNumber}</h1>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">

          {/* Discount Banner */}
          {invoice.isDiscounted && invoice.discountPercent > 0 && (
            <div className="bg-emerald-50 border-b border-emerald-100">
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-700" />
                      <p className="text-emerald-800 text-xs font-semibold uppercase tracking-wider">Early Payment Discount</p>
                    </div>
                    <p className="text-slate-950 text-lg font-bold">
                      {invoice.discountPercent}% off — Save <span className="text-emerald-700">${formatCurrency(invoice.savings)}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500 text-xs">Pay before</p>
                    <p className="text-slate-950 font-semibold text-sm">{invoice.discountDeadline}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Amount Due */}
          <div className="px-6 py-10 text-center border-b border-slate-200">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Amount Due</p>
            <p className="text-5xl font-bold text-slate-950 tracking-tight">
              ${formatCurrency(invoice.total)}
            </p>
            {invoice.isDiscounted && invoice.discountPercent > 0 && (
              <p className="text-sm text-slate-400 mt-2 line-through">
                ${formatCurrency(invoice.originalTotal)}
              </p>
            )}
          </div>

          {/* Invoice Details */}
          <div className="px-6 py-5 border-b border-slate-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wider font-medium mb-1">Billed To</p>
                <p className="font-medium text-slate-950">{invoice.clientName}</p>
                <p className="text-slate-500 text-xs mt-0.5">{invoice.projectName}</p>
              </div>
              <div className="text-right">
                <p className="text-slate-500 text-xs uppercase tracking-wider font-medium mb-1">Due Date</p>
                <p className="font-medium text-slate-950">{invoice.dueDate}</p>
                {invoice.paymentTerms && (
                  <p className="text-slate-500 text-xs mt-0.5">{invoice.paymentTerms}</p>
                )}
              </div>
            </div>
          </div>

          {/* Line Items — driven directly by tracker items when available
              so each row carries its own price + photos + Brian's request
              context (single source of truth, no redundancy). Legacy
              invoices without tracker data fall back to the simple list. */}
          <div className="px-6 py-5 border-b border-slate-200">
            <p className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-4">Line Items</p>

            {invoice.items && invoice.items.length > 0 ? (
              <div className="space-y-4">
                {(() => {
                  let renderedFreeHeader = false;
                  return invoice.items.map((item) => {
                    const cents = (item as any).amount_cents ?? 0;
                    const isFree = cents === 0;
                    const showFreeHeader = isFree && !renderedFreeHeader;
                    if (showFreeHeader) renderedFreeHeader = true;
                    const desc = (item.description || item.source_context || "").replace(/\s+/g, " ").trim();
                    return (
                      <div key={item.id}>
                        {showFreeHeader && (
                          <div className="my-4 -mx-6 px-6 py-2 bg-emerald-50 border-y border-emerald-100">
                            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-emerald-700">
                              Included at no charge — fixes &amp; polish
                            </p>
                          </div>
                        )}
                        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:gap-6">
                          <div className="min-w-0">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="font-mono text-xs font-semibold text-blue-700">#{item.seq_num}</span>
                              <span className={`text-sm font-medium ${isFree ? "text-slate-500" : "text-slate-950"}`}>
                                {item.title}
                              </span>
                              {item.category && (
                                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">
                                  {item.category}
                                </span>
                              )}
                            </div>
                            {desc && (
                              <p className="text-xs text-slate-500 mt-1 leading-5">
                                {desc.slice(0, 240)}{desc.length > 240 ? "…" : ""}
                              </p>
                            )}
                            {(item.attachments?.length > 0 || item.video_url) && (
                              <div className="mt-2.5 flex flex-wrap gap-1.5">
                                {item.attachments.slice(0, 4).map((att) => {
                                  const isImg = att.mime_type?.startsWith("image/");
                                  const isVid = ["video/mp4", "video/webm", "video/ogg"].includes(att.mime_type || "");
                                  return (
                                    <button
                                      key={att.public_url}
                                      type="button"
                                      onClick={() => (isImg || isVid) && setExpandedAttachment(att)}
                                      className="group block overflow-hidden rounded-md border border-slate-200 bg-slate-50 hover:border-blue-300 transition-colors w-16 h-16"
                                      title={att.filename}
                                    >
                                      {isImg ? (
                                        <img src={att.public_url} alt={att.filename} className="h-full w-full object-cover transition group-hover:scale-105" loading="lazy" />
                                      ) : isVid ? (
                                        <video src={att.public_url} className="h-full w-full object-cover" muted playsInline />
                                      ) : (
                                        <div className="flex h-full w-full items-center justify-center text-[9px] font-medium text-slate-500 px-1 text-center">file</div>
                                      )}
                                    </button>
                                  );
                                })}
                                {item.video_url && (
                                  <a
                                    href={item.video_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center rounded-md border border-blue-200 bg-blue-50 px-2 h-16 text-[10px] font-semibold text-blue-700 hover:bg-blue-100"
                                  >
                                    Watch
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="sm:text-right">
                            {isFree ? (
                              <p className="text-xs font-semibold tracking-wider uppercase whitespace-nowrap text-emerald-700 font-mono">
                                $0.00 — Free
                              </p>
                            ) : (
                              <p className="text-sm font-semibold text-slate-950 tabular-nums whitespace-nowrap font-mono">
                                ${formatCurrency(cents / 100)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className="space-y-3">
                {invoice.lineItems.map((item, i) => (
                  <div key={i} className="flex justify-between items-start group">
                    <div className="flex-1 pr-4">
                      <p className="text-sm font-medium text-slate-950">{item.description}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.detail}</p>
                    </div>
                    <p className="text-sm font-semibold text-slate-950 tabular-nums whitespace-nowrap font-mono">
                      ${formatCurrency(item.amount)}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Subtotal / Discount */}
            <div className="mt-5 pt-4 border-t border-slate-200 space-y-2">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-500">Subtotal</p>
                <p className="text-sm font-semibold text-slate-950 tabular-nums font-mono">${formatCurrency(invoice.subtotal)}</p>
              </div>
              {invoice.isDiscounted && invoice.discountPercent > 0 && (
                <div className="flex justify-between items-center">
                  <p className="text-sm font-medium text-emerald-700">{invoice.discountPercent}% Early Payment Discount</p>
                  <p className="text-sm font-semibold text-emerald-700 tabular-nums font-mono">-${formatCurrency(invoice.savings)}</p>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-950">Total Due</p>
                <p className="text-lg font-bold text-slate-950 tabular-nums font-mono">${formatCurrency(invoice.total)}</p>
              </div>
            </div>
          </div>

          {/* Pay Button */}
          <div className="p-6">
            <a
              href={invoice.paymentUrl}
              className="group block w-full text-center py-4 rounded-lg font-semibold text-base transition bg-blue-700 text-white hover:bg-blue-800"
            >
              Pay ${formatCurrency(invoice.total)}
              <ArrowRight className="inline-block ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <p className="text-center text-xs text-slate-500 mt-3">Powered by Stripe — secure card processing</p>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 text-blue-700" /> Secure Payment
          </span>
          <span className="flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-blue-700" /> Invoice {invoice.invoiceNumber}
          </span>
          {invoice.paymentTerms && (
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-blue-700" /> {invoice.paymentTerms} Terms
            </span>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-500 mt-6">
          Better Systems AI &bull; Custom Software & Automation &bull; bettersystems.ai
        </p>
      </div>

      {/* Attachment lightbox */}
      {expandedAttachment && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={expandedAttachment.filename}
          onClick={() => setExpandedAttachment(null)}
        >
          <div className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-3">
              <div className="truncate text-sm font-semibold text-slate-950">{expandedAttachment.filename}</div>
              <button
                type="button"
                onClick={() => setExpandedAttachment(null)}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-700 transition hover:bg-slate-100"
                aria-label="Close media preview"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[calc(92vh-60px)] overflow-auto p-3" onClick={(e) => e.stopPropagation()}>
              {expandedAttachment.mime_type?.startsWith("image/") ? (
                <img
                  src={expandedAttachment.public_url}
                  alt={expandedAttachment.filename}
                  className="mx-auto max-h-[calc(92vh-96px)] w-auto max-w-full rounded-lg object-contain"
                />
              ) : (
                <video
                  src={expandedAttachment.public_url}
                  className="mx-auto max-h-[calc(92vh-96px)] w-auto max-w-full rounded-lg"
                  controls
                  autoPlay
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
